import type { Component, ComputedRef } from 'vue'

import type { ApprovalInboxRow } from '../models/platformAction.types'

/**
 * One place in the approvals queue: a request on its own, or a group another module folded a run
 * of them into. The queue's order is the server's, and an entry sits where its first request sat.
 *
 * `group` is opaque here on purpose — this module renders it through the registered component and
 * never reads it, which is what keeps the dependency one-way (module 32 → 31).
 */
export type ApprovalInboxEntry =
  | { kind: 'row'; key: string; row: ApprovalInboxRow }
  | { kind: 'group'; key: string; group: unknown }

export interface ApprovalInboxGrouping {
  /**
   * Called once from the inbox's `setup`, so its watchers live and die with the list. Receives the
   * current page's rows and answers the queue as entries.
   */
  use: (rows: ComputedRef<ApprovalInboxRow[]>) => { entries: ComputedRef<ApprovalInboxEntry[]> }
  /** Renders one `group` entry: takes the opaque `group` prop, emits `decided` once it acted. */
  component: Component
}

let grouping: ApprovalInboxGrouping | null = null

/**
 * The approvals-queue counterpart of `registerActionDetailBlock`: module 32 folds an insight run's
 * requests into one card through it (F5, ORP-22267). Without a registration every request is its
 * own row, exactly as before.
 */
export function registerApprovalInboxGrouping(next: ApprovalInboxGrouping): void {
  grouping = next
}

export function approvalInboxGrouping(): ApprovalInboxGrouping | null {
  return grouping
}

export function resetApprovalInboxGrouping(): void {
  grouping = null
}
